/**
 * Central API endpoint paths (single source of truth).
 * Do not include baseURL here; baseURL comes from env via apiClient.
 */

export const API_ENDPOINTS = {
  auth: {
    me: "/api/v1/auth/me",
    otpRequest: "/api/v1/auth/otp/request",
    otpVerify: "/api/v1/auth/otp/verify",
    selectRole: "/api/v1/auth/select-role",
    // Password login
    login: "/api/v1/auth/login",
    verify2fa: "/api/v1/auth/verify-2fa",
    refresh: "/api/v1/auth/refresh",
    // Password reset
    forgotPassword: "/api/v1/auth/forgot-password",
    verifyResetOtp: "/api/v1/auth/verify-otp",
    resetPassword: "/api/v1/auth/reset-password",
  },
  students: {
    create: "/api/v1/students",
    list: "/api/v1/students",
    importPreview: "/api/v1/students/import/preview",
    importCommit: "/api/v1/students/import/commit",
    detail: (studentId: string) => `/api/v1/students/${studentId}`,
    notes: (studentId: string) => `/api/v1/students/${studentId}/notes`,
    reportCard: (studentId: string) => `/api/v1/students/${studentId}/report-card`,
  },
  attendance: {
    create: "/api/v1/attendance",
    submit: "/api/v1/attendance/submit",
    list: "/api/v1/attendance",
    update: (
      attendanceId: number,
      schoolId: number,
      studentId: number,
      date: string,
    ) =>
      `/api/v1/attendance/${attendanceId}?school_id=${schoolId}&student_id=${studentId}&date=${date}`,
  },
  marks: {
    record: "/api/v1/marks/record",
    submit: "/api/v1/marks/submit",
    list: "/api/v1/marks", // Add this line
  },
  communications: {
    homework: "/api/v1/communications/homework",
    parentMessage: "/api/v1/communications/parent-messages",
  },
  teachers: {
    list: "/api/v1/teachers",
    create: "/api/v1/teachers",
    updateStatus: (teacherId: number) => `/api/v1/teachers/${teacherId}/status`,
    me: "/api/v1/teachers/me",
    meReadiness: "/api/v1/teachers/me/readiness",
    meContext: "/api/v1/teachers/me/context",
    meTeachingAssignments: "/api/v1/teachers/me/teaching-assignments",
    meAttendanceSection: "/api/v1/teachers/me/attendance-section",
  },
  academicSetup: {
    list: "/api/v1/academic-setup",
  },
  schools: {
    list: "/api/v1/schools",
    create: "/api/v1/schools",
    dashboard: (schoolId: number) => `/api/v1/schools/${schoolId}/dashboard`,
    teachers: (schoolId: number) => `/api/v1/schools/${schoolId}/teachers`,
    students: (schoolId: number) => `/api/v1/schools/${schoolId}/students`,
    staff: (schoolId: number) => `/api/v1/schools/${schoolId}/staff`,
  },
  classes: {
    list: "/api/v1/classes",
    create: "/api/v1/classes",
  },
  subjects: {
    list: "/api/v1/subjects",
    create: "/api/v1/subjects",
  },

  sections: {
    list: "/api/v1/sections",
    create: "/api/v1/sections",
  },
  teachingAssignments: {
    list: "/api/v1/teaching-assignments",
    create: "/api/v1/teaching-assignments",
    history: "/api/v1/teaching-assignments/history",
    substitute: (id: number) => `/api/v1/teaching-assignments/${id}/substitute`,
  },
  studentNotes: {
    list: (studentId: string) => `/api/v1/students/${studentId}/notes`,
    create: (studentId: string) => `/api/v1/students/${studentId}/notes`,
  },

  management: {
    teachersCreate: "/api/v1/management/teachers",
    principal: "/api/v1/management/principal",
    principalRegister: "/api/v1/management/principal/register",
    principalRetryOtp: "/api/v1/management/principal/retry-otp",
    principalHistory: "/api/v1/management/principal/history",
    principalOnboardingStart: "/api/v1/management/principal/onboarding/start",
    principalOnboardingVerify: "/api/v1/management/principal/onboarding/verify",
    principalOnboardingResend: "/api/v1/management/principal/onboarding/resend",
  },
  principal: {
    dashboard: "/api/v1/principal/dashboard",
  },
  cms: {
    byContentType: (contentType: string) => `/api/v1/cms/${contentType}`,
  },
  superadmin: {
    dashboard: "/api/v1/superadmin/dashboard",
    schools: "/api/v1/superadmin/schools",
  },
} as const;
